import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { database } from '../firebase';
import { ref, onValue, update } from 'firebase/database';
import { auth } from '../firebase';
import PlayerWords from './PlayerWords';
import GlobalMessage from './GlobalMessage';
import WordScramble from './WordScramble';
import RevealedLetters from './RevealedLetters';
import AddNewPlayer from './AddNewPlayer';
import Settings from './Settings';
import UserDisplay from './UserDisplay';
import ExplainerPopup from './ExplainerPopup';
import GameOver from './GameOver';
import { calculateGameStats } from './GameOver';
import { submitGameResult } from './utils';
import { burst, scorePop } from '../lib/confetti';
import { invalidLines } from '../lib/copy';

import validWordsUrl from '../validWords.txt';

const buzz = (pattern) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(pattern);
};

const GamePage = () => {
    const { gameId } = useParams();
    const navigate = useNavigate();
    const [players, setPlayers] = useState([]);
    const [currentPlayer, setCurrentPlayer] = useState({});
    const [currentTurn, setCurrentTurn] = useState(0);
    const [letterPool, setLetterPool] = useState([]);
    const [revealedLetters, setRevealedLetters] = useState([]);
    const [previousLetterLength, setPreviousLetterLength] = useState(0);
    const [juggletWord, setJuggletWord] = useState('');
    const [localMessage, setLocalMessage] = useState('');
    const [globalMessage, setGlobalMessage] = useState({});
    const [wordsArray, setWordsArray] = useState('');
    const [playerWords, setPlayerWords] = useState({});
    const [selectedLetters, setSelectedLetters] = useState([]);
    const [selectedWords, setSelectedWords] = useState([]);
    const [currentDelay, setCurrentDelay] = useState(0);
    const [settings, setSettings] = useState({});
    const [winningPlayerID, setWinningPlayerID] = useState('');
    const [gameOverMessage, setGameOverMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [showGameOverModal, setShowGameOverModal] = useState(false);
    const [gameStats, setGameStats] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    const isSolo = !!(settings.solo && settings.solo.isSolo);

    useEffect(() => {
        fetch(validWordsUrl)
            .then(response => response.text())
            .then(text => {
                const words = text.split('\n');
                setWordsArray(words);
            })
            .catch(error => {
                console.error('Error loading word list:', error);
            });
    }, []);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(user => {
            if (!user) {
                navigate(`/login?game=${gameId}`);
            }
            setIsAuthLoading(false);
        });
        return () => unsubscribe();
    }, [navigate, gameId]);

    useEffect(() => {
        if (gameId !== gameId.toUpperCase()) {
            navigate(`/game/${gameId.toUpperCase()}`);
        }

        if (isAuthLoading) return;

        const gameRef = ref(database, `games/${gameId}`);
        const unsubscribe = onValue(gameRef, (snapshot) => {
            const gameData = snapshot.val();
            if (gameData) {
                setPlayers(gameData.players || []);
                setCurrentTurn(gameData.currentTurn || 0);
                setLetterPool(gameData.letterPool || []);
                setRevealedLetters(gameData.revealedLetters || []);
                setPlayerWords(gameData.playerWords || {});
                setGlobalMessage(gameData.globalMessage || {});
                setSettings(gameData.settings || {});
                setWinningPlayerID(gameData.winningPlayerID || "");
                setGameStats(gameData.gameStats || {});
            }
        });

        return () => unsubscribe();
    }, [gameId, navigate, isAuthLoading]);

    useEffect(() => {
        if (revealedLetters.length > previousLetterLength && players[0] && currentPlayer.delay > 0) {
            setCurrentDelay(currentPlayer.delay);
        }

        if (revealedLetters.length < previousLetterLength) {
            setSelectedLetters([]);
            setSelectedWords([]);
            setJuggletWord("");
        }

        setPreviousLetterLength(revealedLetters.length);
    }, [currentPlayer.delay, players, previousLetterLength, revealedLetters]);

    const getPlayerByID = useCallback((id) => {
        for (let player of players) {
            if (player.id === id) {
                return player;
            }
        }
        return null;
    }, [players]);

    useEffect(() => {
        if (players.length > 0) {
            const theCurrentPlayer = getPlayerByID(auth.currentUser.uid);
            if (!theCurrentPlayer) {
                navigate(`/join/${gameId}`);
            } else {
                theCurrentPlayer.email = auth.currentUser.email;
                setCurrentPlayer(theCurrentPlayer);
            }
        }
    }, [players, getPlayerByID, gameId, navigate]);

    useEffect(() => {
        let newMsgTxt = "No winner!";
        if (winningPlayerID !== "") {
            let winningPlayer = getPlayerByID(winningPlayerID);
            newMsgTxt = winningPlayer.name + " wins with a score of " + winningPlayer.score;
        }
        setGameOverMessage(newMsgTxt);
    }, [winningPlayerID, players, getPlayerByID]);

    const handleDelayFinished = () => {
        setCurrentDelay(0);
    };

    const updateScores = (updatePlayerWords) => {
        let lowScore = 100;
        let highScore = -1;
        let winnerID = -1;
        let playersCopy = [...players];

        for (let player of playersCopy) {
            let score = 0;
            if (updatePlayerWords[player.id]) {
                for (let word of updatePlayerWords[player.id]) {
                    score += getWordScore(word);
                }
            }
            player.score = score;
            if (score > highScore) {
                winnerID = player.id;
                highScore = score;
            }
            if (score < lowScore) {
                lowScore = score;
            }
        }

        if (settings.delayType === "automatic") {
            for (let player of playersCopy) {
                player.delay = calcDelay(player.score, lowScore, highScore);
            }
        }

        setPlayers(playersCopy);
        updateFirestore({ players: playersCopy, winningPlayerID: winnerID });
    };

    const getWordScore = (word) => {
        return word.length - 2;
    };

    const calcDelay = (score, lowScore, highScore) => {
        const scalingFactor = 5;
        const increments = 5;
        return Math.floor((score - lowScore) / scalingFactor) * increments;
    };

    const handleRevealLetter = () => {
        const newLetter = selectLetter(letterPool);
        const newRevealedLetters = [newLetter, ...revealedLetters];
        const newLetterPool = [...letterPool];
        let nextTurn = (currentTurn + 1) % players.length;

        if (newLetterPool.length === 0) {
            nextTurn = -1;
        }

        updateFirestore({
            letterPool: newLetterPool,
            revealedLetters: newRevealedLetters,
            currentTurn: nextTurn
        });

        if (settings.solo && settings.solo.isSolo) {
            clearSelection();
        }

        buzz(8);
        setLocalMessage("");
    };

    const handleGameOver = async () => {
        setSelectedLetters([]);
        setSelectedWords([]);
        setJuggletWord('');
        const newGlobalMessage = { 'text': '', 'type': '' };
        const winningPlayer = getPlayerByID(winningPlayerID);
        const stats = await calculateGameStats(winningPlayer, gameStats, players, playerWords, gameId);
        if (settings.solo && settings.solo.isSolo) {
            submitGameResult(winningPlayer, settings.solo);
        }

        updateFirestore({
            currentTurn: -2,
            globalMessage: newGlobalMessage,
            gameStats: stats
        });
        setShowGameOverModal(true);
    };

    const selectLetter = (pool) => {
        return pool.shift();
    };

    const handleJuggletSubmit = async () => {
        setIsSubmitting(true);
        try {
            let newGlobalMessageText = currentPlayer.name + " guessed " + juggletWord;
            let newGlobalMessageType = "Incorrect";
            if (juggletWord.length < 3) {
                setLocalMessage(invalidLines.tooShort);
                buzz([8, 40, 8]);
            } else if (selectedLetters.length === 0 && juggletWord.length === selectedWords[0].word.length) {
                setLocalMessage(invalidLines.needLonger);
                buzz([8, 40, 8]);
            } else if (!isActualWord()) {
                setLocalMessage(invalidLines.notAWord);
                buzz([8, 40, 8]);
            } else {
                const sameWordStems = await hasSameWordStems();
                if (sameWordStems) {
                    setLocalMessage(invalidLines.sameStem);
                    buzz([8, 40, 8]);
                } else {
                    // Word is correct.

                    // 1. Remove used letters both locally and globally
                    removeLettersFromRevealed();
                    let updatedPlayerWords = { ...playerWords };
                    updatedPlayerWords = removeSelectedWords(updatedPlayerWords);

                    // 2. Add new word to current player
                    updatedPlayerWords = addWordForPlayer(currentPlayer.id, juggletWord, updatedPlayerWords);

                    // 3. Update scores
                    updateScores(updatedPlayerWords);

                    // 4. Update Firestore
                    updateFirestore({ playerWords: updatedPlayerWords });
                    setPlayerWords(updatedPlayerWords);

                    // 5. Clear selection
                    clearSelection();

                    // 6. Celebrate
                    const pts = getWordScore(juggletWord);
                    newGlobalMessageText += " (+" + pts + ")";
                    newGlobalMessageType = "Correct";
                    buzz(18);
                    burst({ y: window.innerHeight * 0.5 });
                    scorePop(pts, window.innerWidth / 2, window.innerHeight - 200);
                }
            }
            const newGlobalMessage = { 'text': newGlobalMessageText, 'type': newGlobalMessageType };
            setGlobalMessage(newGlobalMessage);
            updateFirestore({ globalMessage: newGlobalMessage });
            setIsSubmitting(false);
        } catch (error) {
            console.error('Error:', error);
            setIsSubmitting(false);
        }
    };

    const clearSelection = async () => {
        setSelectedLetters([]);
        setSelectedWords([]);
        setLocalMessage("");
        setJuggletWord('');
    };

    const isActualWord = () => {
        return wordsArray.includes(juggletWord.toUpperCase());
    };

    const hasSameWordStems = async () => {
        for (let w of selectedWords) {
            const sameWordStem = await hasSameWordStem(juggletWord, w.word);
            if (sameWordStem) {
                return true;
            }
        }
        return false;
    };

    async function hasSameWordStem(word1, word2) {
        const url = `https://us-west1-jugglet.cloudfunctions.net/have_same_stem?word1=${encodeURIComponent(word1)}&word2=${encodeURIComponent(word2)}`;
        try {
            const response = await fetch(url);
            const data = await response.json();
            return data.have_same_stem;
        } catch (error) {
            console.error('Error:', error);
        }
    }

    const removeLettersFromRevealed = () => {
        let lettersCopy = [...revealedLetters];
        for (let x of selectedLetters) {
            let index = lettersCopy.indexOf(x.letter);
            if (index === -1) {
                console.error('Error: didnt find letter');
            }
            lettersCopy.splice(index, 1);
        }
        setRevealedLetters(lettersCopy);
        updateFirestore({ revealedLetters: lettersCopy });
    };

    const removeSelectedWords = (updatedPlayerWords) => {
        for (let w of selectedWords) {
            updatedPlayerWords = removeWordForPlayer(w.playerID, w.word, updatedPlayerWords);
        }
        return updatedPlayerWords;
    };

    const removeWordForPlayer = (playerID, word, updatedPlayerWords) => {
        if (Object.prototype.hasOwnProperty.call(updatedPlayerWords, playerID)) {
            let i = updatedPlayerWords[playerID].indexOf(word);
            if (i > -1) {
                updatedPlayerWords[playerID].splice(i, 1);
            }
        }
        return updatedPlayerWords;
    };

    const addWordForPlayer = (playerID, word, thePlayerWords) => {
        const updatedPlayerWords = {
            ...thePlayerWords,
            [playerID]: [...(playerWords[playerID] || []), word]
        };
        return updatedPlayerWords;
    };

    const updateFirestore = (updateObj) => {
        const gameRef = ref(database, `games/${gameId}`);
        update(gameRef, updateObj)
            .catch(error => console.error("Error updating Firestore: ", error));
    };

    const handleSelectLetter = (letter, index) => {
        const letterObj = { letter, index };
        const isSelected = selectedLetters.some(l => l.letter === letter && l.index === index);
        if (isSelected) {
            setSelectedLetters(selectedLetters.filter(l => l.index !== index));
            setJuggletWord(juggletWord.replace(letter, ''));
        } else {
            setSelectedLetters([...selectedLetters, letterObj]);
            setJuggletWord(juggletWord + letter);
        }
        buzz(5);
    };

    const handleSelectWord = (word, playerID, index) => {
        const wordObj = { word, playerID, index };
        const isSelected = selectedWords.some(w => w.word === word && w.index === index && w.playerID === playerID);
        if (isSelected) {
            setSelectedWords(selectedWords.filter(w => !(w.index === index && w.playerID === playerID)));
            let newJuggleWord = juggletWord;
            for (let letter of word.split('')) {
                newJuggleWord = newJuggleWord.replace(letter, '');
            }
            setJuggletWord(newJuggleWord);
        } else {
            setSelectedWords([...selectedWords, wordObj]);
            setJuggletWord(juggletWord + word);
        }
        buzz(5);
    };

    const handleUpdatePlayers = (updatedPlayers) => {
        setPlayers(updatedPlayers);
        updateFirestore({ players: updatedPlayers });
    };

    const handleUpdateSettings = (newSettings) => {
        setSettings(newSettings);
        updateFirestore({ settings: newSettings });
    };

    const handleUpdateCurrentPlayer = (updatedCurrentPlayer) => {
        setCurrentPlayer(updatedCurrentPlayer);
        const updatedPlayers = [...players];
        for (let player of updatedPlayers) {
            if (player.id === updatedCurrentPlayer.id) {
                player.name = updatedCurrentPlayer.name;
            }
        }
        setPlayers(updatedPlayers);
        updateFirestore({ players: updatedPlayers });
    };

    const sortedPlayers = [...players].sort((a, b) => b.score - a.score);
    const showTray = currentTurn >= -1 && !(currentDelay > 0);

    return (
        <div className="sg-page">
            <header className="sg-appbar">
                <span
                    className="sg-wordmark"
                    style={{ fontSize: '1.4rem' }}
                    onClick={() => navigate('/')}
                >
                    spellago<span className="sg-wordmark__dot">.</span>
                </span>
                <div className="sg-appbar__cluster">
                    <button className="sg-iconbtn" aria-label="How to play" onClick={() => setHelpOpen(true)}>
                        <span style={{ fontWeight: 900, fontSize: '1.15rem' }}>?</span>
                    </button>
                    {!isSolo && (
                        <Settings
                            players={players}
                            settings={settings}
                            onUpdatePlayers={handleUpdatePlayers}
                            onUpdateSettings={handleUpdateSettings}
                        />
                    )}
                    <UserDisplay currentPlayer={currentPlayer} onUpdateCurrentPlayer={handleUpdateCurrentPlayer} />
                </div>
            </header>

            <ExplainerPopup open={helpOpen} onClose={() => setHelpOpen(false)} />


            <main className="sg-game-main" style={{ paddingBottom: showTray ? 210 : 32 }}>
                <div className="sg-center-col sg-stack" style={{ gap: '18px' }}>
                    <GlobalMessage
                        msg={globalMessage}
                        currentTurn={currentTurn}
                        gameOverMessage={gameOverMessage}
                        handleGameOver={handleGameOver}
                        settings={settings}
                    />

                    <RevealedLetters
                        letters={revealedLetters}
                        onLetterSelect={handleSelectLetter}
                        selectedLetters={selectedLetters}
                        currentDelay={currentDelay}
                        onDelayFinished={handleDelayFinished}
                        currentTurn={currentTurn}
                        currentTurnPlayer={players[currentTurn]}
                        thisPlayer={currentPlayer}
                        onRevealLetter={handleRevealLetter}
                        letterPoolCount={letterPool.length}
                        players={players}
                        settings={settings}
                    />

                    <div style={{ marginTop: '6px' }}>
                        {sortedPlayers.map((player, i) => (
                            <PlayerWords
                                key={player.id}
                                player={player}
                                words={playerWords[player.id] || []}
                                onWordSelect={handleSelectWord}
                                selectedWords={selectedWords}
                                isCurrentPlayer={player.id === currentPlayer.id}
                                isLeader={i === 0 && players.length > 1}
                                currentDelay={currentDelay}
                                currentTurn={currentTurn}
                            />
                        ))}
                        {!isSolo && <AddNewPlayer players={players} />}
                    </div>
                </div>
            </main>

            {showTray && (
                <div className="sg-tray">
                    <div className="sg-tray__inner sg-center-col">
                        {localMessage && (
                            <div key={localMessage} className="sg-toast sg-toast--incorrect sg-anim-shake" style={{ alignSelf: 'center' }}>
                                <span>🫧</span><span>{localMessage}</span>
                            </div>
                        )}
                        <div className="sg-tray__card">
                            <WordScramble
                                word={juggletWord}
                                onWordScramble={setJuggletWord}
                                currentDelay={currentDelay || 0}
                                revealedLetters={revealedLetters}
                                playerWords={playerWords}
                                currentTurn={currentTurn}
                            />
                            <div className="sg-tray__actions">
                                <button
                                    className="sg-btn sg-btn--ghost sg-btn--sm"
                                    onClick={clearSelection}
                                    disabled={!juggletWord || isSubmitting}
                                >
                                    Clear
                                </button>
                                <button
                                    className="sg-btn sg-btn--primary sg-btn--block"
                                    onClick={handleJuggletSubmit}
                                    disabled={!juggletWord || isSubmitting}
                                >
                                    {isSubmitting ? 'Checking…' : 'Enter ⏎'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showGameOverModal && gameStats && (
                <GameOver
                    stats={gameStats}
                    onGameOverClosed={() => setShowGameOverModal(false)}
                />
            )}
        </div>
    );
};

export default GamePage;
